"use client";

/**
 * The files surface's upload, one file at a time, written into the one upload
 * store (uploadStore) the tray reads. Three steps per file, the composer's
 * (AttachmentHandler, PR #3858): ask the server for a signed URL, PUT the bytes
 * with putWithProgress (XHR: fetch cannot report upload progress, and a 500 MB
 * file takes minutes), then confirm. Pure TS, no React: the caller
 * (useUploadFiles) hands in the auth headers and the listing refetch.
 */

import { ANALYTICS_EVENTS, UPLOAD_MAX_BYTES } from "@bash/shared";
import { resilientFetch } from "@/lib/fetch";
import { posthog } from "@/lib/posthog";
import { putWithProgress } from "@/components/message-input/putWithProgress";
import {
  isStorageTooLarge,
  storageRefusedMessage,
  tooLargeMessage,
  uploadPercent,
} from "@/components/message-input/uploadCopy";
import {
  armCancel,
  nextUploadId,
  releaseCancel,
  useUploadStore,
  type UploadRow,
} from "./uploadStore";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

export const couldNotUpload = (filename: string): string =>
  `Couldn't upload ${filename}. Try again.`;

const noRoomFor = (filename: string): string =>
  `There is no room left for ${filename}. Delete some files and try again.`;

/** A failure already worded for the person. Anything else reads as `couldNotUpload`. */
class Refused extends Error {}

/** The session no longer belongs to the batch's owner (signed out, or someone else signed in). */
class NotOwner extends Error {}

/** The live session, read per file: whose it is, and the headers to send as them. */
export interface UploadSession {
  userId: string;
  headers: Record<string, string>;
}

/** The confirm's refusal, read from its structured `error` field (file-routes.handler). */
async function confirmRefusal(res: Response, file: File): Promise<string> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // Not JSON: nothing structured to read, so the plain failure.
  }
  const { error, limitBytes } = (body ?? {}) as {
    error?: unknown;
    limitBytes?: unknown;
  };
  if (error === "too_large") {
    const limit =
      typeof limitBytes === "number" ? limitBytes : UPLOAD_MAX_BYTES;
    return tooLargeMessage(file.name, file.size, limit);
  }
  if (error === "quota_exceeded") return noRoomFor(file.name);
  return couldNotUpload(file.name);
}

interface Job {
  id: string;
  file: File;
  signal: AbortSignal;
}

export interface UploadBatch {
  files: File[];
  folderId: string | null;
  /** The person who started the batch: every file goes up as them or not at all. */
  ownerId: string;
  /** The live session, asked for per file (a long batch can outlive a token); null when signed out. */
  getSession: () => Promise<UploadSession | null>;
  /** After the last file settles: the listing refetch. */
  onSettled: () => void;
}

/**
 * Every file gets its row the moment it is picked, before any byte moves: the
 * tray appears at once. A file over the upload cap is refused right there with
 * the composer's words, and never touches the network.
 */
export async function uploadBatch({
  files,
  folderId,
  ownerId,
  getSession,
  onSettled,
}: UploadBatch): Promise<void> {
  useUploadStore.getState().claim(ownerId);
  const queue: Job[] = [];
  const rows = files.map((file): UploadRow => {
    const id = nextUploadId();
    const base = { id, filename: file.name, sizeBytes: file.size };
    if (file.size > UPLOAD_MAX_BYTES) {
      return {
        ...base,
        phase: "failed",
        error: tooLargeMessage(file.name, file.size, UPLOAD_MAX_BYTES),
      };
    }
    queue.push({ id, file, signal: armCancel(id) });
    return { ...base, phase: "waiting" };
  });
  useUploadStore.getState().add(rows);
  if (queue.length === 0) return;

  // One file at a time, as before: a drop of fifty files is fifty confirms the
  // one-replica server files in turn, never all at once.
  let landed = 0;
  for (const [i, job] of queue.entries()) {
    const outcome = await uploadOne(job, folderId, ownerId, getSession);
    if (outcome === "landed") landed++;
    if (outcome === "stopped") {
      // Signed out, or someone else signed in: nothing more goes up, and the
      // rows still queued go with it rather than waiting forever.
      const { cancel } = useUploadStore.getState();
      for (const rest of queue.slice(i)) cancel(rest.id);
      break;
    }
  }
  if (landed > 0) {
    posthog.capture(ANALYTICS_EVENTS.FILES_FILE_UPLOADED, { count: landed });
  }
  onSettled();
}

/** One file through the three steps: landed in Files, failed, or stopped (not the owner's session). */
async function uploadOne(
  { id, file, signal }: Job,
  folderId: string | null,
  ownerId: string,
  getSession: () => Promise<UploadSession | null>
): Promise<"landed" | "failed" | "stopped"> {
  // Cancelled while it waited: its row is already gone.
  if (signal.aborted) return "failed";
  const { patch } = useUploadStore.getState();
  patch(id, { phase: "uploading", progress: 0 });
  try {
    const session = await getSession();
    if (!session || session.userId !== ownerId) throw new NotOwner();
    const { headers } = session;
    const urlRes = await resilientFetch(`${SERVER_URL}/api/files/upload-url`, {
      method: "POST",
      headers,
      body: JSON.stringify({ filename: file.name, bucket: "uploads" }),
      signal,
    });
    if (!urlRes.ok) throw new Error("Failed to get upload URL");
    // The server's field is `signedUrl` (file-routes.handler). Reading
    // `uploadUrl` once sent every PUT to a garbage URL (incident 08-03).
    const { signedUrl, storagePath } = (await urlRes.json()) as {
      signedUrl?: string;
      storagePath?: string;
    };
    if (!signedUrl) throw new Error("Upload URL missing from server response");

    let shownPercent = -1;
    const put = await putWithProgress(
      signedUrl,
      file,
      file.type || "application/octet-stream",
      (fraction) => {
        // One repaint per whole percent, the chip's own rule.
        const percent = uploadPercent(fraction);
        if (percent === shownPercent) return;
        shownPercent = percent;
        patch(id, { progress: fraction });
      },
      signal
    );
    // Storage refuses an oversize object with HTTP 400 and the 413 INSIDE its
    // JSON body, read as fields (uploadCopy.isStorageTooLarge).
    if (put.status < 200 || put.status >= 300) {
      throw new Refused(
        isStorageTooLarge(put.status, put.body)
          ? storageRefusedMessage(file.name)
          : couldNotUpload(file.name)
      );
    }

    // The bytes are up. The server now files the upload (and copies it to the
    // computer) before it answers, so the row says "Finishing" and drops its
    // cancel: stopping the request here would not stop the filing.
    releaseCancel(id);
    patch(id, { phase: "finishing", progress: 1 });
    const confirm = await resilientFetch(
      `${SERVER_URL}/api/files/confirm-upload`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          storagePath,
          filename: file.name,
          mimeType: file.type || null,
          sizeBytes: file.size || null,
          bucket: "uploads",
          // Upload lands where you are: the current drill-in folder, or
          // loose (null → Uploaded) at the root.
          folderId,
        }),
      }
    );
    if (!confirm.ok) throw new Refused(await confirmRefusal(confirm, file));
    patch(id, { phase: "done" });
    return "landed";
  } catch (err) {
    if (err instanceof NotOwner) return "stopped";
    // A cancel is the person removing the row: it is already gone. Read from
    // the row's own signal, not the error's shape.
    if (!signal.aborted) {
      patch(id, {
        phase: "failed",
        error: err instanceof Refused ? err.message : couldNotUpload(file.name),
      });
    }
    return "failed";
  } finally {
    releaseCancel(id);
  }
}
